import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PartyPopper, ArrowRight, X, Users } from "lucide-react";
import api from "@/lib/api";

const LAUNCH = new Date("2026-10-14T14:00:00+01:00");
const KEY = "pula_launch_strip_dismissed";

// Slim, site-wide launch countdown. Non-floating top strip — never covers content.
// Compact launch countdown pill for embedding in the slim utility bar. Dismissible (shared KEY).
export function LaunchPill() {
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } });
  // Birmingham launch postponed — pill hidden until a new date is confirmed.
  return null;
  // eslint-disable-next-line no-unreachable
  if (hidden) return null;
  const daysLeft = Math.ceil((LAUNCH.getTime() - Date.now()) / 86400000);
  const label = daysLeft > 1 ? `${daysLeft}d to launch` : daysLeft === 1 ? "Launches tomorrow" : daysLeft === 0 ? "Launching today" : "We've launched";
  const dismiss = () => { try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ } setHidden(true); };
  return (
    <span data-testid="launch-pill" className="inline-flex items-center gap-1.5 rounded-full bg-pula-gold text-pula-ink px-2 py-0.5 font-semibold shrink-0">
      <PartyPopper className="w-3 h-3" /> {label}
      <Link to="/get-app" data-testid="launch-pill-cta" className="underline underline-offset-2 hover:opacity-80">Join</Link>
      <button onClick={dismiss} data-testid="launch-pill-dismiss" aria-label="Dismiss launch countdown" className="hover:opacity-70"><X className="w-3 h-3" /></button>
    </span>
  );
}

// Slim, site-wide launch countdown strip (kept for any pages that still use it).
export function LaunchStrip() {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  const [rsvpCount, setRsvpCount] = useState(0);

  useEffect(() => {
    api.get("/launch/rsvp/count").then(({ data }) => setRsvpCount(data.count || 0)).catch(() => {});
  }, []);

  // Birmingham launch postponed — strip hidden until a new date is confirmed.
  return null;

  // eslint-disable-next-line no-unreachable
  if (hidden) return null;

  const daysLeft = Math.ceil((LAUNCH.getTime() - Date.now()) / 86400000);
  const countdown = daysLeft > 1 ? `${daysLeft} days to go` : daysLeft === 1 ? "Tomorrow!" : daysLeft === 0 ? "Happening today!" : "We've launched — join free";
  const dismiss = () => { try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ } setHidden(true); };

  return (
    <div className="bg-pula-green text-white" data-testid="launch-strip">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-x-3 gap-y-1 flex-wrap text-center relative">
        <span className="inline-flex items-center gap-1.5 text-sm font-body">
          <PartyPopper className="w-4 h-4 shrink-0" />
          <span className="font-heading font-bold">Pula launches Wed 14 October</span>
          <span className="hidden sm:inline text-white/85">· Library of Birmingham, Centenary Square</span>
        </span>
        <span className="inline-flex items-center text-xs font-semibold bg-pula-gold text-pula-ink rounded-full px-2.5 py-0.5" data-testid="launch-strip-countdown">
          {countdown}
        </span>
        {rsvpCount > 0 && (
          <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-white/90" data-testid="launch-strip-rsvp-count">
            <Users className="w-3.5 h-3.5" /> {rsvpCount} neighbour{rsvpCount === 1 ? "" : "s"} coming
          </span>
        )}
        <Link to="/get-app" data-testid="launch-strip-cta"
          className="inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-2 hover:text-pula-gold transition-colors">
          Join free <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <button onClick={dismiss} data-testid="launch-strip-dismiss" aria-label="Dismiss launch banner"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full grid place-items-center hover:bg-white/15 text-white/80">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
